import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { DonorReview } from "../apiPath/api";

export const createDonorReview = createAsyncThunk(
  "donorReview/createDonorReview",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await DonorReview.createDonorReview(formData);
      return response.data;
    } catch (error) {
      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create donor review";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

export const getAllDonorReviews = createAsyncThunk(
  "donorReview/getAllDonorReviews",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await DonorReview.getAllDonorReviews(params);
      return response.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Login failed";
      return rejectWithValue(backendMsg);
    }
  }
);

export const deleteDonorReview = createAsyncThunk(
  "donorReview/deleteDonorReview",
  async (id, { rejectWithValue }) => {
    try {
      // Adjust API call as per your API utility
      const response = await DonorReview.deleteDonorReview(id);
      return response.data.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Login failed";
      return rejectWithValue(backendMsg);
    }
  }
);

export const undoDeleteDonorReview = createAsyncThunk(
  "donorReview/undoDeleteDonorReview",
  async (id, { rejectWithValue }) => {
    try {
      const response = await DonorReview.undoDeleteDonorReview(id);
      return response.data.data;
    } catch (error) {
      const backendMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        "Login failed";
      return rejectWithValue(backendMsg);
    }
  }
);

export const updateDonorReview = createAsyncThunk(
  "donorReview/updateDonorReview",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await DonorReview.updateDonorReview(id, data);
      return response.data;
    } catch (error) {
      // Extract complete error message
      const message =
        error.response?.data?.message ||
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update donor review";

      // If there are validation errors, include them
      const validationErrors = error.response?.data?.errors || {};
      const errorDetails = Object.values(validationErrors).join(", ");

      const fullMessage = errorDetails
        ? `${message}. ${errorDetails}`
        : message;

      return rejectWithValue(fullMessage);
    }
  }
);

const donorReviewSlice = createSlice({
  name: "donorReview",
  initialState: {
    loading: false,
    error: null,
    success: false,
    donorReview: [],
    pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
    statusCounts: { all: 0, active: 0, inactive: 0, deleted: 0 },
  },

  reducers: {
    resetDonorReviewState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // Create Donor Review
      .addCase(createDonorReview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createDonorReview.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createDonorReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update Donor Review
      .addCase(updateDonorReview.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDonorReview.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateDonorReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Get All Donor Reviews
      .addCase(getAllDonorReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllDonorReviews.fulfilled, (state, action) => {
        state.loading = false;
        state.donorReview = action.payload.data;
        state.pagination = action.payload.pagination;
        state.statusCounts = action.payload.statusCounts;
        state.error = null;
      })
      .addCase(getAllDonorReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Donor Review
      .addCase(deleteDonorReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDonorReview.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteDonorReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Undo Delete Donor Review
      .addCase(undoDeleteDonorReview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(undoDeleteDonorReview.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(undoDeleteDonorReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetDonorReviewState } = donorReviewSlice.actions;
export default donorReviewSlice.reducer;
